import { useEffect, useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import axiosServices, { fetcher } from 'utils/axios';

import type { GetQrCodesParams, QrCodeResource, QrCodeResponse } from 'types/tables';
import type { AvailableWaiterType } from 'types/waiter';

// ==============================|| QR CODES HOOK ||============================== //

export function useGetQrCodes(params: GetQrCodesParams) {
  const { restaurantId, per_page = 100 } = params;

  const getKey = (pageIndex: number, previousPageData: QrCodeResponse | null) => {
    if (previousPageData) {
      const { meta } = previousPageData;
      const totalPages = meta.last_page;
      if (pageIndex >= totalPages) return null;
    }

    const queryParams = new URLSearchParams();
    queryParams.set('page', (pageIndex + 1).toString());
    queryParams.set('per_page', per_page.toString());

    return `/restaurants/${restaurantId}/qr-codes?${queryParams.toString()}`;
  };

  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite(getKey, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    initialSize: 1
  });

  useEffect(() => {
    if (data && data.length > 0) {
      const firstPage = data[0];
      const totalPages = firstPage?.meta?.last_page || 0;

      if (totalPages > 1 && size < totalPages) {
        setSize(totalPages);
      }
    }
  }, [data, size, setSize]);

  const allQrCodes = useMemo(() => {
    if (!data) return [] as QrCodeResource[];
    return data.flatMap((pageData) => pageData?.data || []) as QrCodeResource[];
  }, [data]);

  const generalQrCode = useMemo(() => {
    return allQrCodes.find((qrCode) => qrCode.type === 'general' || qrCode.table_id === null) ?? null;
  }, [allQrCodes]);

  const qrCodesByTable = useMemo(() => {
    const qrCodesMap: Record<number, QrCodeResource> = {};
    allQrCodes.forEach((qrCode) => {
      if (qrCode.table_id != null && qrCode.type !== 'general') {
        qrCodesMap[qrCode.table_id] = qrCode;
      }
    });
    return qrCodesMap;
  }, [allQrCodes]);

  const memoizedValue = useMemo(() => {
    const firstPage = data?.[0];
    const meta = firstPage?.meta;

    const totalPages = meta ? meta.last_page : 0;
    const hasMorePages = firstPage && meta ? size < totalPages : false;
    const isStillLoading = isLoading || (totalPages > 1 && size < totalPages);

    return {
      qrCodes: allQrCodes,
      generalQrCode,
      qrCodesByTable,
      qrCodesLoading: isStillLoading,
      qrCodesError: error,
      qrCodesValidating: isValidating,
      qrCodesEmpty: !isStillLoading && allQrCodes.length === 0,
      pagination: {
        currentPage: size,
        totalPages: totalPages,
        totalItems: meta?.total || 0,
        perPage: per_page
      },
      loadMore: () => setSize(size + 1),
      hasMore: hasMorePages,
      refetch: mutate
    };
  }, [allQrCodes, generalQrCode, qrCodesByTable, data, error, isLoading, isValidating, size, setSize, per_page, mutate]);

  return memoizedValue;
}

// ==============================|| GENERATE QR CODE HOOK ||============================== //

interface GenerateQrCodeParams {
  restaurantId: number;
  type?: 'table' | 'general';
  tableId?: number;
}

interface GenerateQrCodeResponse {
  success: boolean;
  message: string;
  data?: QrCodeResource;
}

export function useGenerateQrCode() {
  const [isLoading, setIsLoading] = useState(false);

  const generateQrCode = async (params: GenerateQrCodeParams): Promise<GenerateQrCodeResponse> => {
    setIsLoading(true);
    try {
      const isGeneral = params.type === 'general';
      const response = await axiosServices.post(`/restaurants/${params.restaurantId}/qr-codes`, {
        ...(isGeneral ? { type: 'general' } : { table_id: params.tableId })
      });

      mutate((key) => typeof key === 'string' && key.includes('/qr-codes'), undefined, { revalidate: true });

      return {
        success: true,
        message: 'QR code generated successfully',
        data: response?.data?.data || response?.data
      };
    } catch (error: any) {
      console.error('Error generating QR code:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to generate QR code'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { generateQrCode, isLoading };
}

// ==============================|| EXPORT QR CODE HOOK ||============================== //

interface ExportQrCodeParams {
  restaurantId: number;
  qrCodeId: number;
}

interface ExportQrCodeResponse {
  success: boolean;
  message: string;
  data?: Blob;
}

export function useExportQrCode() {
  const [isLoading, setIsLoading] = useState(false);

  const exportQrCode = async (params: ExportQrCodeParams): Promise<ExportQrCodeResponse> => {
    setIsLoading(true);
    try {
      const response = await axiosServices.get(`/restaurants/${params.restaurantId}/qr-codes/${params.qrCodeId}/export`, {
        responseType: 'blob'
      });

      const downloadBlob = (blob: Blob, filename: string) => {
        if (blob.size === 0) {
          throw new Error('Downloaded file is empty');
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return {
          success: true,
          message: 'QR code exported successfully',
          data: blob
        };
      };

      if (response.data instanceof Blob) {
        return downloadBlob(response.data, `qr-code-${params.qrCodeId}.pdf`);
      } else {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        return downloadBlob(blob, `qr-code-${params.qrCodeId}.pdf`);
      }
    } catch (error: any) {
      console.error('Error exporting QR code:', error);

      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to export QR code'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { exportQrCode, isLoading };
}

// ==============================|| DELETE QR CODE HOOK ||============================== //

interface DeleteQrCodeParams {
  restaurantId: number;
  qrCodeId: number;
}

interface DeleteQrCodeResponse {
  success: boolean;
  message: string;
  data?: any;
}

export function useDeleteQrCode() {
  const [isLoading, setIsLoading] = useState(false);

  const deleteQrCode = async (params: DeleteQrCodeParams): Promise<DeleteQrCodeResponse> => {
    setIsLoading(true);
    try {
      await axiosServices.delete(`/restaurants/${params.restaurantId}/qr-codes/${params.qrCodeId}`);

      mutate((key) => typeof key === 'string' && key.includes('/qr-codes'), undefined, { revalidate: true });

      return {
        success: true,
        message: 'QR code deleted successfully',
        data: null
      };
    } catch (error: any) {
      console.error('Error deleting QR code:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to delete QR code'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteQrCode, isLoading };
}

// ==============================|| GET QR SVG (IMAGE) HOOK ||============================== //

interface GetQrSvgParams {
  restaurantId: number;
  qrCodeId: number;
}

export function useGetQrSvg(params?: GetQrSvgParams | null) {
  const key = params?.restaurantId && params?.qrCodeId ? `/restaurants/${params.restaurantId}/qr-codes/${params.qrCodeId}` : null;

  const { data, error, isLoading, mutate } = useSWR<string>(
    key,
    async (url: string) => {
      const response = await axiosServices.get(url, {
        responseType: 'text',
        headers: { Accept: 'image/svg+xml' }
      });
      return String(response.data || '');
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );

  const [svgUrl, setSvgUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!data) {
      setSvgUrl(null);
      return;
    }
    const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(data)}`;
    setSvgUrl(dataUrl);
  }, [data]);

  return { svgUrl, isLoading, error, refetch: mutate };
}

// ==============================|| GET QR RESERVATION HOOK ||============================== //

export interface QrReservationData {
  type: 'table' | 'general';
  reservation_id: number | null;
  waiter_id: number | null;
  restaurant_id: number;
  restaurant_name?: string | null;
}

interface GetQrReservationResponse {
  data: QrReservationData;
}

export function useGetQrReservation(secret: string | null) {
  const key = secret ? `/qr/${secret}/reservation` : null;

  const { data, error, isLoading, mutate } = useSWR<GetQrReservationResponse>(key, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false
  });

  return {
    reservationData: data?.data,
    reservationLoading: isLoading,
    reservationError: error,
    refetch: mutate
  };
}

// ==============================|| GET QR WAITERS HOOK ||============================== //

export interface QrWaitersData {
  qr: {
    type: 'general' | 'table';
    restaurant_id: number;
    restaurant_name: string | null;
  };
  waiters: AvailableWaiterType[];
}

interface GetQrWaitersResponse {
  data: QrWaitersData;
}

export function useGetQrWaiters(secret: string | null) {
  const key = secret ? `/qr/${secret}/waiters` : null;

  const { data, error, isLoading, mutate } = useSWR<GetQrWaitersResponse>(key, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false
  });

  return {
    qrWaitersData: data?.data,
    qrWaitersLoading: isLoading,
    qrWaitersError: error,
    refetchQrWaiters: mutate
  };
}
